// SR-SHARE-LANDING (#321) — public landing for a 72h Slip Repair share link.
//
// Mounted at /r/:token. Anon-readable — no AppShell, no login required.
// Renders the doctrine-safe projection that handleShareGet exposes from
// slip-repair-api: high-level repair outcome + per-leg before/after,
// nothing about HOW edges are found (no scores, weights, internal
// market models, no per-league win rates). Per public_communication_rules.md
// (Voulgaris Edges 4 & 7): outputs only.
//
// Fetch uses the public anon JWT — that satisfies Supabase's vendor
// JWT gate at the edge boundary; the function's own router lets
// share_get through without an authenticated caller.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface ShareLegRow {
  leg_id: string;
  leg_number: number;
  orig_match: string | null;
  orig_market: string | null;
  orig_selection: string | null;
  orig_odds: number | null;
  void_risk: string | null;
  void_condition: string | null;
  sharp_decision: string | null;
  sharp_rep_market: string | null;
  sharp_rep_selection: string | null;
  sharp_rep_odds: number | null;
  sharp_reason: string | null;
  safer_decision: string | null;
  safer_rep_market: string | null;
  safer_rep_selection: string | null;
  safer_rep_odds: number | null;
  safer_reason: string | null;
}

interface ShareSession {
  session_id: string;
  bookmaker: string | null;
  leg_count_original: number | null;
  leg_count_repaired: number | null;
  legs_retained: number | null;
  legs_replaced: number | null;
  legs_flagged: number | null;
  legs_marginal: number | null;
  legs_unresolved: number | null;
  total_odds_original: number | null;
  total_odds_sharp: number | null;
  total_odds_safer: number | null;
  mode_selected: string | null;
  slip_summary: string | null;
  repaired_at: string | null;
}

interface ShareResponse {
  ok: true;
  session: ShareSession;
  legs: ShareLegRow[];
  excluded_markets: { label: string; reason: string }[];
  referral_code: string | null;
  expires_at: string;
}

interface ShareError {
  ok: false;
  error: { code: string; message: string };
}

type FetchState =
  | { kind: "loading" }
  | { kind: "ok"; data: ShareResponse }
  | { kind: "error"; code: string; message: string; status: number };

async function fetchShareReceipt(token: string): Promise<FetchState> {
  const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!url || !anon) {
    return {
      kind: "error",
      code: "config_missing",
      message: "Site misconfigured. Please contact support.",
      status: 500,
    };
  }
  let res: Response;
  try {
    res = await fetch(
      `${url.replace(/\/+$/, "")}/functions/v1/slip-repair-api/share/${encodeURIComponent(token)}`,
      {
        method: "GET",
        headers: { Authorization: `Bearer ${anon}`, apikey: anon },
      },
    );
  } catch (e) {
    return {
      kind: "error",
      code: "network",
      message: e instanceof Error ? e.message : "Network error",
      status: 0,
    };
  }
  let body: ShareResponse | ShareError;
  try {
    body = (await res.json()) as ShareResponse | ShareError;
  } catch {
    return {
      kind: "error",
      code: "bad_response",
      message: "Could not parse server response.",
      status: res.status,
    };
  }
  if (!res.ok || body.ok === false) {
    const err = (body as ShareError).error ?? {
      code: "unknown",
      message: "Unknown error",
    };
    return {
      kind: "error",
      code: err.code,
      message: err.message,
      status: res.status,
    };
  }
  return { kind: "ok", data: body as ShareResponse };
}

function fmtOdds(n: number | null | undefined): string {
  if (typeof n !== "number" || !isFinite(n) || n <= 1) return "—";
  return n.toFixed(2);
}

function fmtCount(n: number | null | undefined): string {
  if (typeof n !== "number" || !isFinite(n)) return "—";
  return String(Math.max(0, Math.round(n)));
}

function fmtExpires(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const now = Date.now();
  const ms = d.getTime() - now;
  if (ms <= 0) return "expired";
  const h = Math.floor(ms / (1000 * 60 * 60));
  if (h >= 24) return `expires in ${Math.floor(h / 24)}d ${h % 24}h`;
  if (h >= 1) return `expires in ${h}h`;
  const m = Math.max(1, Math.floor(ms / (1000 * 60)));
  return `expires in ${m}m`;
}

function decisionPill(decision: string | null | undefined): {
  label: string;
  bg: string;
  fg: string;
} {
  const d = (decision ?? "").toUpperCase();
  if (d === "RETAIN" || d === "KEEP") {
    return { label: "Kept", bg: "#0F3D2E", fg: "#7EE0A8" };
  }
  if (d === "REPLACE") {
    return { label: "Swapped", bg: "#3A2C0F", fg: "#FFC56B" };
  }
  if (d === "MARGINAL") {
    return { label: "Watch", bg: "#2B2B2B", fg: "#D4D4D4" };
  }
  if (d === "FLAG" || d === "FLAGGED") {
    return { label: "Flagged", bg: "#3D1818", fg: "#FF9C9C" };
  }
  return { label: "Unresolved", bg: "#1A1A1A", fg: "#8C8C8C" };
}

function humanise(m: string | null | undefined): string {
  if (!m) return "";
  return m
    .replace(/_/g, " ")
    .replace(/\b(\w)/g, (_, c: string) => c.toUpperCase());
}

export function SlipRepairSharePage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<FetchState>({ kind: "loading" });

  useEffect(() => {
    if (!token) {
      setState({
        kind: "error",
        code: "no_token",
        message: "No share token in URL.",
        status: 400,
      });
      return;
    }
    let cancelled = false;
    void (async () => {
      const r = await fetchShareReceipt(token);
      if (!cancelled) setState(r);
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      style={{
        minHeight: "var(--vh-full)",
        background: "#0A0A0A",
        color: "#E8E8E8",
        fontFamily: "ui-sans-serif, system-ui, -apple-system, sans-serif",
        padding: "32px 16px",
      }}
    >
      <div style={{ maxWidth: 880, margin: "0 auto" }}>
        <Header />
        {state.kind === "loading" && <LoadingPane />}
        {state.kind === "error" && (
          <ErrorPane code={state.code} message={state.message} status={state.status} />
        )}
        {state.kind === "ok" && <Receipt data={state.data} />}
        <Footer />
      </div>
    </div>
  );
}

function Header(): JSX.Element {
  return (
    <header
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 28,
      }}
    >
      <a
        href="/"
        style={{
          color: "#E8E8E8",
          textDecoration: "none",
          fontSize: 22,
          fontWeight: 700,
          letterSpacing: "-0.01em",
        }}
      >
        STRIKZA
      </a>
      <span style={{ fontSize: 12, color: "#8C8C8C", textTransform: "uppercase", letterSpacing: 1 }}>
        Slip Repair · Shared Receipt
      </span>
    </header>
  );
}

function LoadingPane(): JSX.Element {
  return (
    <div
      style={{
        padding: 48,
        border: "1px solid #1F1F1F",
        borderRadius: 14,
        textAlign: "center",
        color: "#8C8C8C",
      }}
    >
      Loading receipt…
    </div>
  );
}

function ErrorPane(props: { code: string; message: string; status: number }): JSX.Element {
  const expired = props.code === "expired" || props.code === "revoked";
  const notFound = props.code === "not_found" || props.status === 404;
  const headline = expired
    ? "This share link has expired"
    : notFound
      ? "Share link not found"
      : "Couldn't load this receipt";
  const sub = expired
    ? "Repair share links are valid for 72 hours. Ask the sender to issue a fresh link."
    : notFound
      ? "Double-check the URL. If it was copied correctly, the link may have been revoked."
      : props.message;

  return (
    <div
      style={{
        padding: "40px 32px",
        border: "1px solid #2A1818",
        background: "#150B0B",
        borderRadius: 14,
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{headline}</div>
      <div style={{ fontSize: 14, color: "#A8A8A8" }}>{sub}</div>
      <div style={{ marginTop: 20 }}>
        <a
          href="/repair"
          style={{
            display: "inline-block",
            padding: "10px 20px",
            background: "#E8E8E8",
            color: "#0A0A0A",
            borderRadius: 8,
            textDecoration: "none",
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Repair your own slip
        </a>
      </div>
    </div>
  );
}

function Receipt(props: { data: ShareResponse }): JSX.Element {
  const s = props.data.session;
  const legs = props.data.legs;
  const expiresLabel = fmtExpires(props.data.expires_at);

  return (
    <article>
      <SummaryCard session={s} expiresLabel={expiresLabel} />
      <LegList legs={legs} mode={(s.mode_selected ?? "sharp") as "sharp" | "safer" | "both"} />
      {props.data.excluded_markets.length > 0 && (
        <ExcludedMarkets items={props.data.excluded_markets} />
      )}
      <CTA referral={props.data.referral_code} />
    </article>
  );
}

function SummaryCard(props: {
  session: ShareSession;
  expiresLabel: string;
}): JSX.Element {
  const s = props.session;
  return (
    <section
      style={{
        padding: "24px 28px",
        border: "1px solid #1F1F1F",
        borderRadius: 14,
        background: "#0F0F0F",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: "#8C8C8C",
              textTransform: "uppercase",
              letterSpacing: 1,
              marginBottom: 4,
            }}
          >
            Repaired slip
          </div>
          <div style={{ fontSize: 20, fontWeight: 600 }}>
            {fmtCount(s.leg_count_repaired)} legs
            {s.bookmaker ? (
              <span style={{ color: "#8C8C8C", fontWeight: 400, fontSize: 14, marginLeft: 8 }}>
                · {s.bookmaker}
              </span>
            ) : null}
          </div>
        </div>
        <span style={{ fontSize: 12, color: "#8C8C8C" }}>{props.expiresLabel}</span>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
        <Stat label="Original odds" value={fmtOdds(s.total_odds_original)} muted />
        <Stat label="Sharp repair" value={fmtOdds(s.total_odds_sharp)} accent="#7EE0A8" />
        <Stat label="Safer repair" value={fmtOdds(s.total_odds_safer)} accent="#9CC2FF" />
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 10,
          marginTop: 16,
          fontSize: 12,
        }}
      >
        <MiniStat label="Kept" value={fmtCount(s.legs_retained)} />
        <MiniStat label="Swapped" value={fmtCount(s.legs_replaced)} />
        <MiniStat label="Watch" value={fmtCount(s.legs_marginal)} />
        <MiniStat label="Unresolved" value={fmtCount(s.legs_unresolved)} />
      </div>

      {s.slip_summary && (
        <p
          style={{
            marginTop: 16,
            fontSize: 13,
            color: "#C8C8C8",
            lineHeight: 1.55,
            background: "#161616",
            padding: 12,
            borderRadius: 10,
          }}
        >
          {s.slip_summary}
        </p>
      )}
    </section>
  );
}

function Stat(props: { label: string; value: string; muted?: boolean; accent?: string }): JSX.Element {
  return (
    <div
      style={{
        padding: "12px 14px",
        background: "#161616",
        borderRadius: 10,
        border: "1px solid #1F1F1F",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "#8C8C8C",
          textTransform: "uppercase",
          letterSpacing: 1,
          marginBottom: 4,
        }}
      >
        {props.label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 600,
          color: props.muted ? "#9C9C9C" : props.accent ?? "#E8E8E8",
        }}
      >
        {props.value}
      </div>
    </div>
  );
}

function MiniStat(props: { label: string; value: string }): JSX.Element {
  return (
    <div
      style={{
        padding: "8px 10px",
        background: "#161616",
        borderRadius: 8,
        textAlign: "center",
      }}
    >
      <div style={{ color: "#8C8C8C", fontSize: 10, textTransform: "uppercase", letterSpacing: 1 }}>
        {props.label}
      </div>
      <div style={{ fontSize: 16, fontWeight: 600, marginTop: 2 }}>{props.value}</div>
    </div>
  );
}

function LegList(props: { legs: ShareLegRow[]; mode: "sharp" | "safer" | "both" }): JSX.Element {
  if (props.legs.length === 0) {
    return (
      <section
        style={{
          padding: 24,
          border: "1px solid #1F1F1F",
          borderRadius: 14,
          textAlign: "center",
          color: "#8C8C8C",
          marginBottom: 16,
        }}
      >
        No legs in this slip.
      </section>
    );
  }
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
      {props.legs.map((leg) => (
        <LegCard key={leg.leg_id} leg={leg} mode={props.mode} />
      ))}
    </section>
  );
}

function LegCard(props: { leg: ShareLegRow; mode: "sharp" | "safer" | "both" }): JSX.Element {
  const l = props.leg;
  // The shared receipt always shows BOTH tier suggestions when present.
  // The "mode_selected" only nudges which one renders first.
  const safer = (l.safer_decision ?? "").toUpperCase();
  const sharp = (l.sharp_decision ?? "").toUpperCase();
  const safPill = decisionPill(safer);
  const shrpPill = decisionPill(sharp);

  return (
    <div
      style={{
        padding: "14px 16px",
        border: "1px solid #1F1F1F",
        borderRadius: 12,
        background: "#0F0F0F",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 8,
          gap: 12,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ color: "#8C8C8C", fontSize: 11 }}>Leg {l.leg_number}</div>
          <div
            style={{
              fontWeight: 600,
              fontSize: 15,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={l.orig_match ?? ""}
          >
            {l.orig_match ?? "—"}
          </div>
          <div style={{ color: "#A8A8A8", fontSize: 13, marginTop: 2 }}>
            {humanise(l.orig_market) || "—"}
            {l.orig_selection ? ` · ${humanise(l.orig_selection)}` : ""}
            {l.orig_odds ? `  @  ${fmtOdds(l.orig_odds)}` : ""}
          </div>
        </div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginTop: 8,
        }}
      >
        <TierBlock
          tier="Sharp"
          pill={shrpPill}
          repMarket={l.sharp_rep_market}
          repSelection={l.sharp_rep_selection}
          repOdds={l.sharp_rep_odds}
          decision={sharp}
        />
        <TierBlock
          tier="Safer"
          pill={safPill}
          repMarket={l.safer_rep_market}
          repSelection={l.safer_rep_selection}
          repOdds={l.safer_rep_odds}
          decision={safer}
        />
      </div>
    </div>
  );
}

function TierBlock(props: {
  tier: string;
  pill: { label: string; bg: string; fg: string };
  repMarket: string | null;
  repSelection: string | null;
  repOdds: number | null;
  decision: string;
}): JSX.Element {
  const showReplacement = props.decision === "REPLACE" && props.repMarket;
  return (
    <div
      style={{
        padding: 10,
        background: "#161616",
        borderRadius: 8,
        border: "1px solid #1F1F1F",
        fontSize: 13,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span style={{ color: "#8C8C8C", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
          {props.tier}
        </span>
        <span
          style={{
            background: props.pill.bg,
            color: props.pill.fg,
            padding: "2px 8px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
          }}
        >
          {props.pill.label}
        </span>
      </div>
      {showReplacement ? (
        <div style={{ color: "#E8E8E8" }}>
          {humanise(props.repMarket)}
          {props.repSelection ? ` · ${humanise(props.repSelection)}` : ""}
          {props.repOdds ? `  @  ${fmtOdds(props.repOdds)}` : ""}
        </div>
      ) : (
        <div style={{ color: "#9C9C9C" }}>—</div>
      )}
    </div>
  );
}

function ExcludedMarkets(props: { items: { label: string; reason: string }[] }): JSX.Element {
  return (
    <section
      style={{
        padding: "16px 18px",
        border: "1px solid #1F1F1F",
        borderRadius: 12,
        background: "#0F0F0F",
        marginBottom: 16,
        fontSize: 12,
        color: "#A8A8A8",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "#8C8C8C",
          textTransform: "uppercase",
          letterSpacing: 1,
          marginBottom: 8,
        }}
      >
        Markets STRIKZA does not repair
      </div>
      <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.6 }}>
        {props.items.slice(0, 5).map((it, i) => (
          <li key={i}>
            <span style={{ color: "#D4D4D4" }}>{it.label}</span>
            {it.reason ? <span style={{ color: "#8C8C8C" }}> — {it.reason}</span> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function CTA(props: { referral: string | null }): JSX.Element {
  const repairHref = props.referral ? `/repair?ref=${encodeURIComponent(props.referral)}` : "/repair";
  return (
    <section
      style={{
        marginTop: 8,
        marginBottom: 24,
        padding: "20px 24px",
        background: "linear-gradient(180deg, #131313 0%, #0F0F0F 100%)",
        border: "1px solid #1F1F1F",
        borderRadius: 14,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        gap: 16,
        flexWrap: "wrap",
      }}
    >
      <div style={{ minWidth: 240 }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Repair your own slip</div>
        <div style={{ fontSize: 13, color: "#A8A8A8" }}>
          Upload it, see the same before/after — bookmaker-agnostic.
        </div>
      </div>
      <a
        href={repairHref}
        style={{
          padding: "10px 20px",
          background: "#E8E8E8",
          color: "#0A0A0A",
          borderRadius: 8,
          textDecoration: "none",
          fontWeight: 600,
          fontSize: 14,
          whiteSpace: "nowrap",
        }}
      >
        Try it free
      </a>
    </section>
  );
}

function Footer(): JSX.Element {
  return (
    <footer
      style={{
        marginTop: 24,
        textAlign: "center",
        color: "#5C5C5C",
        fontSize: 11,
        lineHeight: 1.6,
      }}
    >
      <div>
        Shared receipts are read-only and expire 72 hours after issue.
      </div>
      <div style={{ marginTop: 4 }}>
        Outputs only. STRIKZA does not publish how the underlying model finds its edges.
      </div>
    </footer>
  );
}
